import { CSSProperties, FC, Fragment, useMemo } from "react";
import { Alignment, Stack } from "@fluentui/react";
import { css, ISignalServiceBase, stringUtils, useComponentLogger, useTheme } from "cayo.ui";
import { WidgetTitle } from "@cayo/core/components/Widgets/common";
import { Entity, PropertyAnnotation } from "@cayo/shared/api/types/cayo-graph";
import { IScheme } from "@cayo/shared/api/types/schema";
import { bindingsRegexes } from "@cayo/shared/scheme/bindings/regexes";
import useService from "@cayo/shared/services/hooks/useService";
import { allRendereres, rendererNames } from "./renderers";
import { getSchemeRenderer } from "./renderers/renderers-utils";
import { ILayoutHierarchy, layoutRenderers } from "./renderers/renderers.layout";

type IGenericSchemeProps = IScheme & {
  data?: any;
  signals?: ISignalServiceBase<any, any>;
  refreshKey?: string;
  onRefresh?: () => void;
  propertyAnnotations?: PropertyAnnotation[];
};

const GenericScheme: FC<IGenericSchemeProps & { object?: Entity; style?: CSSProperties }> = (
  props
) => {
  const {
    items,
    layout,
    data,
    type,
    childrenGap,
    signals,
    userRoles,
    style,
    onRefresh,
    name,
    propertyAnnotations,
  } = props;

  const log = useComponentLogger(GenericScheme, name!);
  const actionBuilder = useService("actionBuilder");
  const theme = useTheme();

  const auxRenderParams = useMemo(
    () => ({
      actionBuilder,
      theme,
      signals,
      refreshKey: props.refreshKey,
      userRoles,
      parentName: props?.name,
      propertyAnnotations,
    }),
    [props?.refreshKey, userRoles, props?.name, signals, propertyAnnotations]
  );

  const result = useMemo(() => {
    if (!items?.length) {
      return [];
    }

    let result = items.map<ILayoutHierarchy>((c, i) => ({
      render: () => {
        const renderer = allRendereres[stringUtils.dashedToLowCase(c.type!) as rendererNames];

        // TODO: optimize. move this object to a cache
        const cProps = c as any;
        cProps.data = data;
        cProps.object = props?.object;
        cProps.signals = signals;
        cProps.onRefresh = onRefresh;
        cProps.propertyAnnotations = props?.propertyAnnotations;

        if (userRoles) {
          cProps.userRoles = userRoles;
        }

        const key = (c.name || c?.type) + (data?.id || "");
        return renderer && <Fragment key={key}>{renderer(c, auxRenderParams)}</Fragment>;
      },
      name: c.name,
      index: i,
    }));

    layout?.forEach((l) => {
      const intersection = items?.filter((f) => l.items?.includes(f.name!));
      if (!intersection || !intersection.length) {
        throw new Error("Invalid schema");
      }

      log.debug("layout " + l.type + " will contain ", intersection);

      const index = items!.findIndex((f) => f.name! === intersection[0].name);
      const excludedIds = intersection.map((i) => i.name);
      const resultIndex = result.findIndex((r) => r.index === index);

      let children = result?.filter((f) => excludedIds.includes(f.name)); //.map((r) => r.render);

      if (children && children.length > 0 && children.length !== l.items?.length) {
        children = l.items!.map<ILayoutHierarchy>((i, ii) => {
          let item = children!.find((c) => c.name === i) as ILayoutHierarchy;
          if (item) {
            item.index = ii;
          } else {
            const spacerProps = { key: "spacer" + ii };

            let wellknownLayoutType = i === "-" ? "spacer" : "";

            if (!wellknownLayoutType) {
              const match = i.match(bindingsRegexes.pixels);
              if (match) {
                wellknownLayoutType = "pixels";
                (spacerProps as any).width = match[0];
              } else {
                wellknownLayoutType = stringUtils.dashedToLowCase(i.toLowerCase());
              }
            }

            item = {
              render: () => {
                const r = getSchemeRenderer(wellknownLayoutType);

                if (!r) {
                  log.error(`renderer ${wellknownLayoutType} not found`);
                  return <Fragment />;
                }

                return r(spacerProps);
              },
              index: ii,
              name: wellknownLayoutType + ii,
            };
          }
          return item;
        });
      }

      const lConfig: ILayoutHierarchy = {
        // @ts-expect-error TS7053 TODO: refactor render.
        render: () => layoutRenderers[l.type!](l, children),
        index,
      };

      result[resultIndex] = lConfig;

      result = result.filter((f) => !excludedIds.includes(f.name));
    });

    return result;
  }, [items, layout]);

  const stackProps = useMemo(() => {
    return { tokens: { childrenGap: childrenGap } };
  }, [childrenGap]);

  const stackStyle = useMemo(
    () => ({
      maxHeight: "100%",
      width: "100%",
      height: "100%",
      ...(style || {}),
    }),
    [style]
  );

  if (!items?.length) {
    const renderer = type && getSchemeRenderer(stringUtils.dashedToLowCase(type));
    return renderer ? renderer(props) : null;
  }

  return (
    <Stack
      style={stackStyle}
      verticalAlign={props.verticalAlign as Alignment}
      horizontal={false}
      horizontalAlign={props.justifyContent as Alignment}
      tokens={stackProps.tokens}
    >
      {/* {title && (
        <WidgetTitle
          variant="large"
          styles={{
            root: { textAlign: "left", flexGrow: "0 !important", paddingBottom: 8 },
          }}
        >
          {title}
        </WidgetTitle>
      )} */}
      {result?.map((r) => r.render())}
    </Stack>
  );
};

export const SchemeSection: FC<IScheme> = ({ title, ...other }) => {
  return (
    <Stack className={css.p20}>
      {title && (
        <WidgetTitle
          variant="large"
          styles={{
            root: { textAlign: "left", flexGrow: "0 !important", paddingBottom: 16 },
          }}
        >
          {title}
        </WidgetTitle>
      )}
      <GenericScheme {...other} />
    </Stack>
  );
};

export default GenericScheme;
